import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Notification {
  id: string;
  title: string;
  body: string;
  timestamp: number;
  data?: Record<string, any>;
  read?: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, "id" | "timestamp">>
    ) => {
      const now = Date.now();
      const DEDUPLICATION_WINDOW_MS = 5000; // 5 seconds

      // Check for duplicate notifications within the deduplication window
      // A duplicate is defined as having the same title, body, and data
      const isDuplicate = state.notifications.some((existing) => {
        const timeDiff = now - existing.timestamp;
        if (timeDiff > DEDUPLICATION_WINDOW_MS) {
          return false; // Too old to be a duplicate
        }

        // Compare title and body
        if (
          existing.title !== action.payload.title ||
          existing.body !== action.payload.body
        ) {
          return false;
        }

        // Compare data objects (deep comparison for common fields)
        const existingData = existing.data || {};
        const newData = action.payload.data || {};

        // Check if both have the same keys and values for common notification fields
        const dataKeys = new Set([
          ...Object.keys(existingData),
          ...Object.keys(newData),
        ]);

        for (const key of dataKeys) {
          if (existingData[key] !== newData[key]) {
            return false;
          }
        }

        return true; // It's a duplicate
      });

      // Only add if it's not a duplicate
      if (!isDuplicate) {
        const notification: Notification = {
          ...action.payload,
          id: `${Date.now()}-${Math.random()}`,
          timestamp: now,
          read: false,
        };

        state.notifications.push(notification);
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach(n => n.read = true);
      state.unreadCount = 0;
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
  },
});

export const { 
  addNotification, 
  removeNotification, 
  markAsRead, 
  markAllAsRead, 
  clearAllNotifications 
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
